/**
 * Compositing: shape layers, chroma key, masks, blend modes, LUTs,
 * stabilization and adjustment layers. Settings live in a clip's `ext`
 * (see ext.ts); the preview shader and the export filters both follow the
 * definitions here so they stay in parity.
 */

import { canvasPaint, type GradientCanvas } from './paint';

// ---------------------------------------------------------------------------
// Blend modes (W3C compositing definitions; the export uses ffmpeg `blend`
// with the backdrop as the first input, which computes the same thing)
// ---------------------------------------------------------------------------

export const BLEND_MODES = [
  { id: 'normal', label: 'Normal', ffmpeg: '' },
  { id: 'multiply', label: 'Multiply', ffmpeg: 'multiply' },
  { id: 'screen', label: 'Screen', ffmpeg: 'screen' },
  { id: 'overlay', label: 'Overlay', ffmpeg: 'overlay' },
  { id: 'softlight', label: 'Soft light', ffmpeg: 'softlight' },
  { id: 'hardlight', label: 'Hard light', ffmpeg: 'hardlight' },
  { id: 'add', label: 'Add (linear dodge)', ffmpeg: 'addition' },
  { id: 'lighten', label: 'Lighten', ffmpeg: 'lighten' },
  { id: 'darken', label: 'Darken', ffmpeg: 'darken' },
  { id: 'difference', label: 'Difference', ffmpeg: 'difference' },
] as const;
export type BlendMode = (typeof BLEND_MODES)[number]['id'];
export const BLEND_MODE_INDEX: Record<string, number> = Object.fromEntries(BLEND_MODES.map((b, i) => [b.id, i]));

/** One channel of a blend mode: backdrop `b`, layer colour `s` (0..1). */
export function blendChannel(mode: string, b: number, s: number): number {
  switch (mode) {
    case 'multiply':
      return b * s;
    case 'screen':
      return b + s - b * s;
    case 'overlay':
      return b < 0.5 ? 2 * b * s : 1 - 2 * (1 - b) * (1 - s);
    case 'hardlight':
      return s < 0.5 ? 2 * b * s : 1 - 2 * (1 - b) * (1 - s);
    case 'softlight': {
      if (s <= 0.5) return b - (1 - 2 * s) * b * (1 - b);
      const d = b <= 0.25 ? ((16 * b - 12) * b + 4) * b : Math.sqrt(b);
      return b + (2 * s - 1) * (d - b);
    }
    case 'add':
      return Math.min(1, b + s);
    case 'lighten':
      return Math.max(b, s);
    case 'darken':
      return Math.min(b, s);
    case 'difference':
      return Math.abs(b - s);
    default:
      return s;
  }
}

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export const SHAPE_TYPES = ['rectangle', 'ellipse', 'triangle', 'line', 'star'] as const;
export type ShapeType = (typeof SHAPE_TYPES)[number];

export interface ShapeProps {
  type: ShapeType;
  /** Size in project pixels (a line goes from the left to the right edge, centred vertically). */
  width: number;
  height: number;
  /** Fill colour or gradient (paint.ts; '' = no fill). */
  fill: string;
  /** Outline colour or gradient, and width in project pixels (0 = none). */
  stroke: string;
  strokeWidth: number;
  /** Corner radius for rectangles (px). */
  radius: number;
}

export const DEFAULT_SHAPE: ShapeProps = { type: 'rectangle', width: 600, height: 120, fill: '#ffffff', stroke: '#000000', strokeWidth: 0, radius: 0 };

/** Shape presets for the editor and MCP. */
export const SHAPE_PRESETS: { id: string; label: string; shape: Partial<ShapeProps> }[] = [
  { id: 'box', label: 'Rectangle', shape: { type: 'rectangle', width: 600, height: 340 } },
  { id: 'rounded', label: 'Rounded box', shape: { type: 'rectangle', width: 600, height: 340, radius: 40 } },
  { id: 'circle', label: 'Circle', shape: { type: 'ellipse', width: 320, height: 320 } },
  { id: 'outline', label: 'Outline box', shape: { type: 'rectangle', width: 600, height: 340, fill: '', stroke: '#ffffff', strokeWidth: 8, radius: 16 } },
  { id: 'bar', label: 'Lower-third bar', shape: { type: 'rectangle', width: 900, height: 110, fill: '#7c3aed', radius: 12 } },
  { id: 'progress', label: 'Progress bar', shape: { type: 'rectangle', width: 1600, height: 16, fill: '#22d3ee', radius: 8 } },
  { id: 'line', label: 'Line', shape: { type: 'line', width: 600, height: 8, fill: '', stroke: '#ffffff', strokeWidth: 8 } },
  { id: 'triangle', label: 'Triangle', shape: { type: 'triangle', width: 300, height: 260 } },
  { id: 'star', label: 'Star', shape: { type: 'star', width: 300, height: 300, fill: '#facc15' } },
];

/** Bitmap size (project px) of a shape, including its outline. */
export function shapeBox(s: ShapeProps): { width: number; height: number } {
  const pad = Math.ceil(s.strokeWidth / 2) + 1;
  return { width: Math.max(1, Math.round(s.width + 2 * pad)), height: Math.max(1, Math.round(s.height + 2 * pad)) };
}

/** The subset of the 2D canvas API used to draw shapes (browser and @napi-rs/canvas). */
export interface ShapeCanvas {
  beginPath(): void;
  closePath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  arcTo(x1: number, y1: number, x2: number, y2: number, r: number): void;
  ellipse(x: number, y: number, rx: number, ry: number, rotation: number, start: number, end: number): void;
  fill(): void;
  stroke(): void;
  fillStyle: unknown;
  strokeStyle: unknown;
  lineWidth: number;
  lineJoin: unknown;
  lineCap: unknown;
  createLinearGradient: GradientCanvas['createLinearGradient'];
  createRadialGradient: GradientCanvas['createRadialGradient'];
}

/** Draws a shape into a canvas of shapeBox(s) × scale pixels. */
export function drawShape(ctx: ShapeCanvas, s: ShapeProps, scale: number) {
  const pad = Math.ceil(s.strokeWidth / 2) + 1;
  const x0 = pad * scale;
  const y0 = pad * scale;
  const w = s.width * scale;
  const h = s.height * scale;
  ctx.beginPath();
  switch (s.type) {
    case 'ellipse':
      ctx.ellipse(x0 + w / 2, y0 + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      break;
    case 'triangle':
      ctx.moveTo(x0 + w / 2, y0);
      ctx.lineTo(x0 + w, y0 + h);
      ctx.lineTo(x0, y0 + h);
      ctx.closePath();
      break;
    case 'line':
      ctx.moveTo(x0, y0 + h / 2);
      ctx.lineTo(x0 + w, y0 + h / 2);
      break;
    case 'star': {
      const cx = x0 + w / 2;
      const cy = y0 + h / 2;
      for (let i = 0; i < 10; i++) {
        const r = i % 2 ? 0.4 : 1;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        const px = cx + Math.cos(a) * (w / 2) * r;
        const py = cy + Math.sin(a) * (h / 2) * r;
        if (i) ctx.lineTo(px, py);
        else ctx.moveTo(px, py);
      }
      ctx.closePath();
      break;
    }
    default: {
      const r = Math.max(0, Math.min(s.radius * scale, w / 2, h / 2));
      ctx.moveTo(x0 + r, y0);
      ctx.arcTo(x0 + w, y0, x0 + w, y0 + h, r);
      ctx.arcTo(x0 + w, y0 + h, x0, y0 + h, r);
      ctx.arcTo(x0, y0 + h, x0, y0, r);
      ctx.arcTo(x0, y0, x0 + w, y0, r);
      ctx.closePath();
    }
  }
  if (s.fill && s.type !== 'line') {
    ctx.fillStyle = canvasPaint(ctx, s.fill, x0, y0, w, h);
    ctx.fill();
  }
  const sw = s.type === 'line' ? Math.max(1, s.strokeWidth || s.height) : s.strokeWidth;
  if (sw > 0) {
    ctx.strokeStyle = canvasPaint(ctx, s.type === 'line' ? s.stroke || s.fill || '#ffffff' : s.stroke, x0, y0, w, h);
    ctx.lineWidth = sw * scale;
    ctx.lineJoin = 'round';
    ctx.lineCap = s.type === 'line' ? 'round' : 'butt';
    ctx.stroke();
  }
}

// ---------------------------------------------------------------------------
// Chroma key (same formula as ffmpeg's colorkey + despill)
// ---------------------------------------------------------------------------

export interface ChromaKey {
  /** Key colour, '#rrggbb'. */
  color: string;
  /** 0.01..1: how far from the key colour still counts as background. */
  similarity: number;
  /** 0..1: soft edge width beyond `similarity` (0 = hard edge). */
  smoothness: number;
  /** 0..1: removes the key colour's spill from what remains. */
  spill: number;
}

export const DEFAULT_KEY: ChromaKey = { color: '#00ff00', similarity: 0.3, smoothness: 0.1, spill: 0.5 };

export function hexToRgb01(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  const v = m ? parseInt(m[1], 16) : 0;
  return [((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255];
}

/** Whether a key colour's spill is green (else blue) — ffmpeg despill has those two. */
export function keySpillIsGreen(color: string): boolean {
  const [, g, b] = hexToRgb01(color);
  return g >= b;
}

/** Alpha and spill-suppressed colour for one pixel (r, g, b in 0..1). */
export function applyKey(k: ChromaKey, r: number, g: number, b: number): { r: number; g: number; b: number; a: number } {
  const [kr, kg, kb] = hexToRgb01(k.color);
  const diff = Math.sqrt(((r - kr) ** 2 + (g - kg) ** 2 + (b - kb) ** 2) / 3);
  const a = k.smoothness > 0.0001 ? Math.min(1, Math.max(0, (diff - k.similarity) / k.smoothness)) : diff > k.similarity ? 1 : 0;
  if (k.spill > 0) {
    if (keySpillIsGreen(k.color)) {
      const map = Math.max(0, g - (r * 0.5 + b * 0.5));
      g = Math.max(0, g - k.spill * map);
    } else {
      const map = Math.max(0, b - (r * 0.5 + g * 0.5));
      b = Math.max(0, b - k.spill * map);
    }
  }
  return { r, g, b, a };
}

// ---------------------------------------------------------------------------
// Masks (in the clip's visible box: 0..1 from its top-left corner)
// ---------------------------------------------------------------------------

export interface MaskSpec {
  shape: 'rectangle' | 'ellipse';
  /** Box of the mask, fractions of the clip box. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Soft edge in fractions of the clip box's shorter side. */
  feather: number;
  invert: boolean;
}

export const DEFAULT_MASK: MaskSpec = { shape: 'ellipse', x: 0.1, y: 0.1, width: 0.8, height: 0.8, feather: 0.05, invert: false };

/**
 * Mask opacity at pixel (px, py) of a W×H box. Signed distance to the
 * mask edge (inside positive), softened across `feather`.
 */
export function maskAlpha(m: MaskSpec, px: number, py: number, W: number, H: number): number {
  const x0 = m.x * W;
  const y0 = m.y * H;
  const x1 = (m.x + m.width) * W;
  const y1 = (m.y + m.height) * H;
  let d: number;
  if (m.shape === 'ellipse') {
    const rx = Math.max(1e-6, (x1 - x0) / 2);
    const ry = Math.max(1e-6, (y1 - y0) / 2);
    const nx = (px - (x0 + rx)) / rx;
    const ny = (py - (y0 + ry)) / ry;
    d = (1 - Math.sqrt(nx * nx + ny * ny)) * Math.min(rx, ry);
  } else {
    d = Math.min(px - x0, x1 - px, py - y0, y1 - py);
  }
  const f = m.feather * Math.min(W, H);
  const a = f > 0.5 ? Math.min(1, Math.max(0, d / f + 0.5)) : d >= 0 ? 1 : 0;
  return m.invert ? 1 - a : a;
}

// ---------------------------------------------------------------------------
// LUTs (.cube)
// ---------------------------------------------------------------------------

export const LUT_MIME = 'application/x-cube-lut';
export const MAX_LUT_BYTES = 20 * 1024 * 1024;

export function lutMimeForExt(ext: string): string | null {
  return ext.toLowerCase() === 'cube' ? LUT_MIME : null;
}

export interface CubeLut {
  size: number;
  /** RGB triplets, red fastest, then green, then blue (as in the file). */
  data: Float32Array;
  domainMin: [number, number, number];
  domainMax: [number, number, number];
}

/** Parses a 3D .cube LUT (Adobe/Resolve format). */
export function parseCube(text: string): CubeLut {
  let size = 0;
  let domainMin: [number, number, number] = [0, 0, 0];
  let domainMax: [number, number, number] = [1, 1, 1];
  const values: number[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const parts = line.split(/\s+/);
    const key = parts[0].toUpperCase();
    if (key === 'LUT_3D_SIZE') size = Number(parts[1]);
    else if (key === 'DOMAIN_MIN') domainMin = parts.slice(1, 4).map(Number) as [number, number, number];
    else if (key === 'DOMAIN_MAX') domainMax = parts.slice(1, 4).map(Number) as [number, number, number];
    else if (key === 'LUT_1D_SIZE') throw new Error('1D LUTs are not supported; use a 3D .cube LUT');
    else if (/^[-+.\d]/.test(key)) {
      if (parts.length < 3) continue;
      values.push(Number(parts[0]), Number(parts[1]), Number(parts[2]));
    }
  }
  if (!(size >= 2 && size <= 129)) throw new Error('Not a 3D .cube LUT (missing LUT_3D_SIZE)');
  if (values.length !== size ** 3 * 3) throw new Error(`The LUT has ${values.length / 3} entries; expected ${size ** 3}`);
  if (values.some((v) => !Number.isFinite(v))) throw new Error('The LUT contains invalid numbers');
  return { size, data: Float32Array.from(values), domainMin, domainMax };
}

/** The LUT mixed with the identity: intensity 0 = no change, 1 = the full LUT. */
export function mixCube(lut: CubeLut, intensity: number): CubeLut {
  const { size } = lut;
  const out = new Float32Array(lut.data.length);
  const t = Math.min(1, Math.max(0, intensity));
  let i = 0;
  for (let b = 0; b < size; b++) {
    for (let g = 0; g < size; g++) {
      for (let r = 0; r < size; r++) {
        const id = [r, g, b].map((v, c) => lut.domainMin[c] + (v / (size - 1)) * (lut.domainMax[c] - lut.domainMin[c]));
        for (let c = 0; c < 3; c++, i++) out[i] = id[c] + (lut.data[i] - id[c]) * t;
      }
    }
  }
  return { ...lut, data: out };
}

export function cubeToText(lut: CubeLut): string {
  const lines = [`LUT_3D_SIZE ${lut.size}`, `DOMAIN_MIN ${lut.domainMin.join(' ')}`, `DOMAIN_MAX ${lut.domainMax.join(' ')}`];
  for (let i = 0; i < lut.data.length; i += 3) lines.push(`${lut.data[i].toFixed(6)} ${lut.data[i + 1].toFixed(6)} ${lut.data[i + 2].toFixed(6)}`);
  return lines.join('\n') + '\n';
}
